/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import {
    Card,
    CardContent,
    CircularProgress,
    Stack,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { InventoryItem, InventoryItemWithAvailable, StorageLocation } from "../../utils/Interfaces";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";

import { useWindowDimensions } from "../../utils/Hooks";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { getMyInventories } from "../tech-work-order/technicianWorkOrderFunctions";
import { listStorageLocationsFromInventories } from "../../utils/Functions";

const MyInventory = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();

    let mobile: boolean = width <= 600;

    type NestedInventoryItem = {
        storageLocation: StorageLocation;
        inventories: InventoryItem[];
    };

    const [nestedInventories, setNestedInventories] = useState<NestedInventoryItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [materialQuery, setMaterialQuery] = useState<string>("");

    function inventoryMatchesQuery(inventory: InventoryItem): boolean {
        if (!materialQuery) {
            return true;
        }

        return (
            inventory.material.name.toLowerCase().includes(materialQuery.toLowerCase()) ||
            (inventory.material.manufacturer ?? "").toLowerCase().includes(materialQuery.toLowerCase()) ||
            inventory.material.part_number.toLowerCase().includes(materialQuery.toLowerCase()) ||
            (inventory.material.notes ?? "").toLowerCase().includes(materialQuery.toLowerCase())
        );
    }

    useEffect(() => {
        function setAsNestedInventory(inventories: InventoryItem[]): void {
            let storageLocations: StorageLocation[] = listStorageLocationsFromInventories(inventories);
            let nestedInventories: NestedInventoryItem[] = [];

            storageLocations.map((storageLocation: StorageLocation): null => {
                nestedInventories.push({
                    storageLocation: storageLocation,
                    inventories: inventories.filter(
                        (inventory: InventoryItem) => inventory.storage_location.id === storageLocation.id
                    ),
                });
                return null;
            });

            setNestedInventories(nestedInventories);
        }
        getMyInventories(dispatch, setAsNestedInventory, setLoading);
    }, [dispatch]);

    return (
        <Box>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "My Parts/Materials", route: "/materials/tech-transfer" },
                ]}
            />

            {loading && (
                <Stack direction="row" justifyContent="center">
                    <CircularProgress size={40} sx={{ mt: 20 }} />
                </Stack>
            )}

            {!loading && (
                <Box>
                    <TextField
                        id="inventory-query"
                        name="inventory-query"
                        size="small"
                        fullWidth
                        value={materialQuery}
                        onChange={(event) => setMaterialQuery(event.target.value)}
                        label="Material Search"
                        sx={{ mb: 3, mt: 1 }}
                    />

                    {nestedInventories.map((nestedInventory: NestedInventoryItem, index: number) => {
                        let matchingInventories: InventoryItem[] = nestedInventory.inventories.filter(
                            (inventory: InventoryItem) => inventoryMatchesQuery(inventory)
                        );

                        return (
                            <Card sx={{ mb: 2 }}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        sx={{ mb: 1, ml: mobile ? 0 : 1 }}
                                        textAlign={mobile ? "center" : "left"}
                                    >
                                        {nestedInventory.storageLocation.name}
                                    </Typography>

                                    <TableContainer>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ width: "40%" }}>
                                                    <Typography fontWeight="bold">Name</Typography>
                                                </TableCell>
                                                <TableCell sx={{ width: "40%" }}>
                                                    <Typography fontWeight="bold">{mobile ? "Mfr." : "Manufacturer"}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ width: "50%" }}>
                                                    <Typography fontWeight="bold">Part #</Typography>
                                                </TableCell>
                                                <TableCell sx={{ width: "50%" }}>
                                                    <Typography fontWeight="bold">Stock</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {matchingInventories.map((inventory: InventoryItem, subIndex: number) => {
                                                return (
                                                    <TableRow sx={{ mb: 2 }} key={subIndex}>
                                                        <TableCell>
                                                            <Typography variant="subtitle2">{inventory.material.name}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="subtitle2">
                                                                {inventory.material.manufacturer}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="subtitle2">
                                                                {inventory.material.part_number}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Tooltip
                                                                title={
                                                                    (inventory as InventoryItemWithAvailable).available <
                                                                    inventory.target
                                                                        ? "Stock below target"
                                                                        : "Stock at or above target"
                                                                }
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    color={
                                                                        (inventory as InventoryItemWithAvailable).available <
                                                                        inventory.target
                                                                            ? "warning.main"
                                                                            : "inherit"
                                                                    }
                                                                >
                                                                    {(inventory as InventoryItemWithAvailable).available}
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </TableContainer>
                                    {matchingInventories.length === 0 && (
                                        <Typography variant="subtitle2" sx={{ mt: 3 }} color="info.main">
                                            {materialQuery !== "" ? "No matching parts or materials." : "No parts or materials"}
                                        </Typography>
                                    )}
                                    {matchingInventories.length > 0 && (
                                        <Typography variant="subtitle2" sx={{ mt: 3 }} color="info.main">
                                            {materialQuery !== ""
                                                ? `${matchingInventories.length} matching parts/materials found.`
                                                : `${matchingInventories.length} total parts/materials.`}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default MyInventory;
